import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { privacyPolicy, termsOfService } from "./privacyPolicy";

const Field = ({ label, id, type = "text", value, onChange, placeholder, autoComplete, icon, focusedField, setFocusedField }) => (
  <div className="space-y-1.5">
    <label htmlFor={id} className="block text-xs font-semibold tracking-widest uppercase text-neutral-500 dark:text-neutral-400">
      {label}
    </label>
    <div className="relative">
      {icon && (
        <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-neutral-400 pointer-events-none">{icon}</span>
      )}
      <input
        id={id}
        type={type}
        autoComplete={autoComplete}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        onFocus={() => setFocusedField(id)}
        onBlur={() => setFocusedField(null)}
        required
        className={`
          w-full rounded-xl border bg-white dark:bg-neutral-800
          px-4 py-3.5 sm:py-3 text-sm
          text-neutral-900 dark:text-neutral-100 placeholder-neutral-400
          transition-all duration-200 outline-none
          ${icon ? "pl-10" : ""}
          ${focusedField === id
            ? "border-green-500 ring-2 ring-green-500/20 shadow-sm"
            : "border-neutral-200 dark:border-neutral-700 hover:border-neutral-300 dark:hover:border-neutral-600"
          }
        `}
      />
    </div>
  </div>
);

// ── Legal document modal ────────────────────────────────────────────────────
const LEGAL_DOCS = {
  terms:   { title: "Terms of Service", content: termsOfService },
  privacy: { title: "Privacy Policy",   content: privacyPolicy },
};

const LegalModal = ({ docKey, onClose }) => {
  const doc = LEGAL_DOCS[docKey];
  if (!doc) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      style={{ background: "rgba(0,0,0,0.55)" }}
      onClick={onClose}
    >
      <div
        className="relative w-full sm:max-w-lg bg-white dark:bg-neutral-900 rounded-t-2xl sm:rounded-2xl shadow-2xl flex flex-col"
        style={{ maxHeight: "85dvh" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-200 dark:border-neutral-700 flex-shrink-0">
          <h2 className="text-sm font-bold text-neutral-900 dark:text-white tracking-tight">{doc.title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="w-7 h-7 rounded-full flex items-center justify-center text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Scrollable body */}
        <div className="overflow-y-auto flex-1 px-5 py-4">
          <pre className="text-xs text-neutral-600 dark:text-neutral-400 whitespace-pre-wrap font-sans leading-relaxed">
            {doc.content.trim()}
          </pre>
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-neutral-200 dark:border-neutral-700 flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="w-full py-3 rounded-xl text-sm font-semibold text-white bg-green-500 hover:bg-green-600 active:scale-[0.98] transition-all duration-200 shadow-md shadow-green-500/25"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

const OTP_SESSION_KEY = "metisel_otp_session";
const saveOtpSession = ({ email, name, username, expiresAt }) => {
  try {
    localStorage.setItem(
      OTP_SESSION_KEY,
      JSON.stringify({
        email,
        name,
        username,
        expiresAt
      })
    );
  } catch (_) {}
};

const loadOtpSession = () => {
  try {
    const raw = localStorage.getItem(OTP_SESSION_KEY);
    if (!raw) return null;
    const session = JSON.parse(raw);
    if (!session?.email || !session?.expiresAt) return null;
    // Discard if already expired
    if (new Date(session.expiresAt).getTime() <= Date.now()) {
      localStorage.removeItem(OTP_SESSION_KEY);
      return null;
    }
    return session;
  } catch (_) {
    return null;
  }
};

const clearOtpSession = () => {
  try { localStorage.removeItem(OTP_SESSION_KEY); } catch (_) {}
};

export const AccountView = ({ onSignIn }) => {
  const navigate = useNavigate();

  const OTP_VALIDITY_SECONDS = 10 * 60;

  // ── Restore OTP session on mount if one exists ───────────────────────────
  const restoredSession = loadOtpSession();

  const [step, setStep] = useState(restoredSession ? "otp" : "form");

  const [name, setName] = useState(restoredSession?.name ?? "");
  const [username, setUsername] = useState(restoredSession?.username ?? "");
  const [email, setEmail] = useState(restoredSession?.email ?? "");
 const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [message, setMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [focusedField, setFocusedField] = useState(null);

  // ── Legal agreement ──────────────────────────────────────────────────────
  const [agreed, setAgreed] = useState(false);
  const [legalModal, setLegalModal] = useState(null); // null | "terms" | "privacy"

  // OTP step state
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [otpMessage, setOtpMessage] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendNotice, setResendNotice] = useState("");
  const [expiresAt, setExpiresAt] = useState(
    restoredSession ? new Date(restoredSession.expiresAt).getTime() : null
  );
  const [secondsLeft, setSecondsLeft] = useState(0);
  const otpRefs = useRef([]);
  const lastAttemptedCode = useRef("");

  // Auto-focus first OTP input on restore
  useEffect(() => {
    if (restoredSession) {
      setTimeout(() => otpRefs.current[0]?.focus(), 50);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validateUsername = (u) => /^[a-zA-Z0-9._]{3,}$/.test(u);
  const validatePassword = (pwd) => /^(?=.*[0-9])(?=.*[!@#$%^&*])(?=.*[a-zA-Z]).{8,}$/.test(pwd);
  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  const getPasswordStrength = (pwd) => {
    if (!pwd) return null;
    const checks = [pwd.length >= 8, /[0-9]/.test(pwd), /[!@#$%^&*]/.test(pwd), /[a-zA-Z]/.test(pwd), pwd.length >= 12];
    const score = checks.filter(Boolean).length;
    if (score <= 2) return { label: "Weak", color: "bg-red-400", width: "w-1/4" };
    if (score === 3) return { label: "Fair", color: "bg-yellow-400", width: "w-2/4" };
    if (score === 4) return { label: "Good", color: "bg-blue-400", width: "w-3/4" };
    return { label: "Strong", color: "bg-green-500", width: "w-full" };
  };

  const strength = getPasswordStrength(password);

  useEffect(() => {
    if (step !== "otp" || !expiresAt) return;
    const tick = () => {
      const remaining = Math.max(0, Math.round((expiresAt - Date.now()) / 1000));
      setSecondsLeft(remaining);
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [step, expiresAt]);

  const formatTime = (totalSeconds) => {
    const m = Math.floor(totalSeconds / 60);
    const s = totalSeconds % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  const codeExpired = step === "otp" && expiresAt !== null && secondsLeft <= 0;

  const trimmedName = name.trim();
  const trimmedUsername = username.trim();
  const trimmedEmail = email.trim();

  const completeRegistration = () => {
    clearOtpSession();
    localStorage.setItem("displayName", trimmedName);
    setSuccessMessage("Account created successfully!");
    setTimeout(() => { if (onSignIn) onSignIn(); else navigate("/login"); }, 1400);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("");
    setSuccessMessage("");

    if (!agreed) {
      setMessage("Please accept the Terms of Service and Privacy Policy to continue.");
      return;
    }

    setLoading(true);

    if (!trimmedName || !trimmedUsername || !trimmedEmail || !password || !confirmPassword) {
      setMessage("All fields are required"); setLoading(false); return;
    }
    if (!validateUsername(trimmedUsername)) {
      setMessage("Username must be at least 3 characters (letters, numbers, . _)"); setLoading(false); return;
    }
    if (!validateEmail(trimmedEmail)) {
      setMessage("Invalid email address"); setLoading(false); return;
    }
    if (!validatePassword(password)) {
      setMessage("Password must be 8+ characters with a number and symbol"); setLoading(false); return;
    }
    if (password !== confirmPassword) {
      setMessage("Passwords do not match"); setLoading(false); return;
    }

    try {
      const res = await axios.post("/api/create-account", { name: trimmedName, username: trimmedUsername, email: trimmedEmail, password, confirmPassword });

      if (res.data?.requiresVerification) {
        const serverExpiresAt = res.data?.expiresAt
          ? new Date(res.data.expiresAt).getTime()
          : Date.now() + OTP_VALIDITY_SECONDS * 1000;

        // ── Persist session so refresh restores the OTP step ──
        saveOtpSession({
          email: trimmedEmail,
          name: trimmedName,
          username: trimmedUsername,
          expiresAt: new Date(serverExpiresAt).toISOString(),
        });

        setOtp(["", "", "", "", "", ""]);
        setOtpMessage("");
        setResendNotice("");
        lastAttemptedCode.current = "";
        setExpiresAt(serverExpiresAt);
        setStep("otp");
        setTimeout(() => otpRefs.current[0]?.focus(), 50);
      } else {
        completeRegistration();
      }
    } catch (err) {
      setMessage(err.response?.data?.message || "Account creation failed");
    }
    setLoading(false);
  };
const verifyCode = async (code) => {
    setOtpMessage("");
    setResendNotice("");

    if (codeExpired) {
      setOtpMessage("This code has expired. Request a new one below.");
      return;
    }
    if (code.length !== 6) {
      setOtpMessage("Enter the 6-digit code");
      return;
    }

    setVerifying(true);
    try {
      await axios.post("/api/create-account/verify", { email: trimmedEmail, code });
      completeRegistration();
    } catch (err) {
      const data = err.response?.data;

      // ✅ Only trust the server's expiresAt when it explicitly sends one
      // (e.g. on a wrong-code response). Never invent an expiry ourselves —
      // that was wiping out a still-valid countdown on ANY failed request
      // (network blips, wrong digit, server hiccup), making a fresh code
      // look "expired" instantly.
      if (data?.expiresAt) {
        setExpiresAt(new Date(data.expiresAt).getTime());
      }

      if (!err.response) {
        setOtpMessage("Network error — please check your connection and try again.");
      } else {
        setOtpMessage(data?.message || "Verification failed. Please try again.");
      }
    }
    setVerifying(false);
  };
  const handleOtpChange = (index, value) => {
    const digit = value.replace(/[^0-9]/g, "").slice(-1);
    const next = [...otp];
    next[index] = digit;

    lastAttemptedCode.current = "";

    setOtp(next);
    if (digit && index < 5) otpRefs.current[index + 1]?.focus();
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    const pasted = e.clipboardData.getData("text").replace(/[^0-9]/g, "").slice(0, 6);
    if (!pasted) return;
    e.preventDefault();
    const next = [...otp];
    for (let i = 0; i < 6; i++) next[i] = pasted[i] || "";
    setOtp(next);
    lastAttemptedCode.current = "";
    const lastFilled = Math.min(pasted.length, 6) - 1;
    otpRefs.current[lastFilled >= 0 ? lastFilled : 0]?.focus();
  };

  useEffect(() => {
    const code = otp.join("");
    if (
      step === "otp" &&
      !successMessage &&
      code.length === 6 &&
      !verifying &&
      !codeExpired &&
      lastAttemptedCode.current !== code
    ) {
      lastAttemptedCode.current = code;
      verifyCode(code);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otp, step, codeExpired]);

  const handleVerify = async (e) => {
    e.preventDefault();
    const code = otp.join("");
    lastAttemptedCode.current = code;
    await verifyCode(code);
  };

  const handleResend = async () => {
    setOtpMessage("");
    setResendNotice("");
    setResending(true);
    try {
      const res = await axios.post("/api/create-account", { name: trimmedName, username: trimmedUsername, email: trimmedEmail, password, confirmPassword: password });
      const serverExpiresAt = res.data?.expiresAt
        ? new Date(res.data.expiresAt).getTime()
        : Date.now() + OTP_VALIDITY_SECONDS * 1000;

      // ── Update persisted session with fresh expiry ──
      saveOtpSession({
        email: trimmedEmail,
        name: trimmedName,
        username: trimmedUsername,
        expiresAt: new Date(serverExpiresAt).toISOString(),
      });

      setOtp(["", "", "", "", "", ""]);
      lastAttemptedCode.current = "";
      setExpiresAt(serverExpiresAt);
      setResendNotice("A new code has been sent to your email.");
      otpRefs.current[0]?.focus();
    } catch (err) {
      setOtpMessage(err.response?.data?.message || "Could not resend code");
    }
    setResending(false);
  };

  const handleBackToForm = () => {
    clearOtpSession();
    setStep("form");
    setOtpMessage("");
    setResendNotice("");
  };

  return (
        <div
      className="h-screen w-full flex overflow-hidden bg-neutral-50 dark:bg-neutral-900"
      style={{ height: "100dvh" }}
    >
      {/* Legal document modal */}
      {legalModal && <LegalModal docKey={legalModal} onClose={() => setLegalModal(null)} />}

      {/* LEFT PANEL */}
      <div className="hidden lg:flex lg:w-[45%] xl:w-[40%] flex-col relative overflow-hidden bg-gradient-to-br from-green-700 via-green-600 to-emerald-500">
        <div className="absolute -top-24 -left-24 w-80 h-80 rounded-full bg-white/5 blur-2xl" />
        <div className="absolute top-1/3 -right-16 w-64 h-64 rounded-full bg-white/5 blur-2xl" />
        <div className="absolute -bottom-20 left-1/4 w-72 h-72 rounded-full bg-emerald-400/20 blur-2xl" />
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "radial-gradient(circle, white 1px, transparent 1px)", backgroundSize: "28px 28px" }} />
        <div className="relative z-10 flex flex-col justify-between h-full p-10">
        <div className="flex justify-center items-center gap-3">
  <div className="w-10 h-10 rounded-full overflow-hidden bg-white shadow-lg">
    <img
      src="/logo192.png"
      alt="Metisel"
      className="w-full h-full object-cover"
    />
  </div>

  <h1 className="text-3xl font-bold text-white tracking-tight select-none">
    Metisel
  </h1>
</div>
          <div className="space-y-5">
            <h2 className="text-white text-4xl font-bold leading-tight tracking-tight">Start your<br />journey<br />today.</h2>
            <p className="text-white/70 text-sm leading-relaxed max-w-xs">Create an account and join a community of people sharing what matters.</p>
            <div className="flex flex-col gap-2 pt-1">
              {["Free to join, forever", "Connect with friends & followers", "Share posts, photos & more"].map((text, i) => (
                <div key={i} className="flex items-center gap-2.5">
                  <div className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                    <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                  </div>
                  <span className="text-white/80 text-sm">{text}</span>
                </div>
              ))}
            </div>
          </div>
          <p className="text-white/40 text-xs">© {new Date().getFullYear()} Metisel. All rights reserved.</p>
        </div>
      </div>

      {/* RIGHT PANEL */}
      <div
        className="flex-1 flex items-start justify-center overflow-y-auto"
        style={{
          padding: "env(safe-area-inset-top, 24px) 16px env(safe-area-inset-bottom, 24px)",
          WebkitOverflowScrolling: "touch",
        }}
      >
        <div className="w-full max-w-sm py-8 sm:py-10">
          {/* Mobile logo */}
<div className="flex lg:hidden justify-center mb-8">
  <div className="w-12 h-12 rounded-full overflow-hidden bg-white shadow-lg shadow-green-500/30">
    <img
      src="/logo192.png"
      alt="Metisel"
      className="w-full h-full object-cover"
    />
  </div>
</div>
          {step === "form" && (
          <div className="text-center">
  <h1 className="text-3xl font-bold text-neutral-900 dark:text-white">
    Create your account
  </h1>

  <p className="mt-2 text-neutral-500 dark:text-neutral-400">
    Join Metisel and get started in just a minute.
  </p>
</div>
          )}

          {step === "otp" && !successMessage && (
            <div className="mb-8">
              <h1 className="text-2xl font-bold text-neutral-900 dark:text-white tracking-tight">Verify your email</h1>
              <p className="mt-1.5 text-sm text-neutral-500 dark:text-neutral-400">
                Enter the 6-digit code we sent to <span className="font-medium text-neutral-700 dark:text-neutral-300">{trimmedEmail}</span>
              </p>
            </div>
          )}

          <div className="bg-white dark:bg-neutral-800 rounded-2xl shadow-sm border border-neutral-200 dark:border-neutral-700 p-5 sm:p-6">

            {/* ============== REGISTRATION FORM ============== */}
            {step === "form" && (
              <>
                {message && (
                  <div className="mb-5 flex items-start gap-2.5 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 px-4 py-3">
                    <svg className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                    <p className="text-sm text-red-600 dark:text-red-400">{message}</p>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                  <Field label="Full Name" id="name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Jane Doe" autoComplete="name" focusedField={focusedField} setFocusedField={setFocusedField}
                    icon={<svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M5.121 17.804A9 9 0 1112 21a9 9 0 01-6.879-3.196z"/></svg>}
                  />
                <Field label="Username" id="username" value={username} onChange={(e) => setUsername(e.target.value)} placeholder="jane_doe" autoComplete="off" focusedField={focusedField} setFocusedField={setFocusedField}
                    icon={<svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/></svg>}
                  />
                <Field label="Email" id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@example.com" autoComplete="username" focusedField={focusedField} setFocusedField={setFocusedField}
                    icon={<svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg>}
                  />

                  {/* Password */}
                  <div className="space-y-1.5">
                    <label htmlFor="password" className="block text-xs font-semibold tracking-widest uppercase text-neutral-500 dark:text-neutral-400">Password</label>
                    <div className="relative">
                      <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-neutral-400 pointer-events-none">
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>
                      </span>
                      <input
                        id="password" type={showPassword ? "text" : "password"} autoComplete="new-password"
                        value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Min. 8 chars, number & symbol"
                        onFocus={() => setFocusedField("password")} onBlur={() => setFocusedField(null)} required
                        className={`w-full rounded-xl border bg-white dark:bg-neutral-800 pl-10 pr-11 py-3.5 sm:py-3 text-sm text-neutral-900 dark:text-neutral-100 placeholder-neutral-400 transition-all duration-200 outline-none ${focusedField === "password" ? "border-green-500 ring-2 ring-green-500/20 shadow-sm" : "border-neutral-200 dark:border-neutral-700 hover:border-neutral-300 dark:hover:border-neutral-600"}`}
                      />
                      <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 transition-colors p-1 touch-manipulation">
                        {showPassword
                          ? <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"/></svg>
                          : <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/><path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/></svg>
                        }
                      </button>
                    </div>
                    {password && strength && (
                      <div className="pt-1 space-y-1">
                        <div className="h-1 w-full rounded-full bg-neutral-100 dark:bg-neutral-700 overflow-hidden">
                          <div className={`h-full rounded-full transition-all duration-500 ${strength.color} ${strength.width}`} />
                        </div>
                        <p className="text-xs text-neutral-400">Strength: <span className={`font-semibold ${strength.label === "Weak" ? "text-red-500" : strength.label === "Fair" ? "text-yellow-500" : strength.label === "Good" ? "text-blue-500" : "text-green-500"}`}>{strength.label}</span></p>
                      </div>
                    )}
                  </div>

                  {/* Confirm Password */}
                  <div className="space-y-1.5">
                    <label htmlFor="confirmPassword" className="block text-xs font-semibold tracking-widest uppercase text-neutral-500 dark:text-neutral-400">Confirm Password</label>
                    <div className="relative">
                      <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-neutral-400 pointer-events-none">
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/></svg>
                      </span>
                      <input
                        id="confirmPassword" type={showPassword ? "text" : "password"} autoComplete="new-password"
                        value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Repeat your password"
                        onFocus={() => setFocusedField("confirmPassword")} onBlur={() => setFocusedField(null)} required
                        className={`w-full rounded-xl border bg-white dark:bg-neutral-800 pl-10 pr-4 py-3.5 sm:py-3 text-sm text-neutral-900 dark:text-neutral-100 placeholder-neutral-400 transition-all duration-200 outline-none ${confirmPassword && confirmPassword !== password ? "border-red-400 ring-2 ring-red-400/20" : confirmPassword && confirmPassword === password ? "border-green-500 ring-2 ring-green-500/20" : focusedField === "confirmPassword" ? "border-green-500 ring-2 ring-green-500/20 shadow-sm" : "border-neutral-200 dark:border-neutral-700 hover:border-neutral-300 dark:hover:border-neutral-600"}`}
                      />
                      {confirmPassword && (
                        <span className="absolute right-3.5 top-1/2 -translate-y-1/2">
                          {confirmPassword === password
                            ? <svg className="w-4 h-4 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/></svg>
                            : <svg className="w-4 h-4 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"/></svg>
                          }
                        </span>
                      )}
                    </div>
                  </div>

                  {/* ── Agreement checkbox ─────────────────────────────────── */}
                  <div className="pt-1">
                    <label className="flex items-start gap-3 cursor-pointer group">
                      <div className="relative flex-shrink-0 mt-0.5">
                        <input
                          type="checkbox"
                          checked={agreed}
                          onChange={(e) => setAgreed(e.target.checked)}
                          className="sr-only"
                        />
                        <div
                          className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all duration-200 ${
                            agreed
                              ? "bg-green-500 border-green-500"
                              : "border-neutral-300 dark:border-neutral-600 group-hover:border-green-400"
                          }`}
                        >
                          {agreed && (
                            <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </div>
                      </div>
                      <span className="text-xs text-neutral-500 dark:text-neutral-400 leading-relaxed">
                        By creating an account and selecting this checkbox, I confirm that I am at least{" "}
                        <span className="font-semibold text-neutral-700 dark:text-neutral-300">18 years old</span>
                        {" "}and agree to the{" "}
                        <button type="button" onClick={(e) => { e.preventDefault(); setLegalModal("terms"); }} className="text-green-600 dark:text-green-400 font-semibold hover:underline touch-manipulation">Terms of Service</button>
                        {" "}and{" "}
                        <button type="button" onClick={(e) => { e.preventDefault(); setLegalModal("privacy"); }} className="text-green-600 dark:text-green-400 font-semibold hover:underline touch-manipulation">Privacy Policy</button>
                        .
                      </span>
                    </label>
                  </div>

                  <div className="pt-1">
                    <button
                      type="submit" disabled={loading || !agreed}
                      className={`w-full py-3.5 sm:py-3 rounded-xl text-sm font-semibold text-white transition-all duration-200 tracking-wide touch-manipulation ${
                        loading || !agreed
                          ? "bg-green-300 dark:bg-green-800 cursor-not-allowed opacity-60"
                          : "bg-green-500 hover:bg-green-600 active:scale-[0.98] shadow-md shadow-green-500/25 hover:shadow-lg hover:shadow-green-500/30"
                      }`}
                    >
                      {loading ? (
                        <span className="flex items-center justify-center gap-2">
                          <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
                          Creating account…
                        </span>
                      ) : "Create Account"}
                    </button>
                  </div>
                </form>

                <p className="mt-5 text-center text-sm text-neutral-500 dark:text-neutral-400">
                  Already have an account?{" "}
                  <button type="button" onClick={() => { if (onSignIn) onSignIn(); else navigate("/login"); }} className="text-green-600 dark:text-green-400 font-semibold hover:underline touch-manipulation">
                    Sign in
                  </button>
                </p>
              </>
            )}

            {/* ============== OTP VERIFICATION ============== */}
            {step === "otp" && !successMessage && (
              <>
                {otpMessage && (
                  <div className="mb-5 flex items-start gap-2.5 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 px-4 py-3">
                    <svg className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                    <p className="text-sm text-red-600 dark:text-red-400">{otpMessage}</p>
                  </div>
                )}
                {resendNotice && (
                  <div className="mb-5 flex items-start gap-2.5 rounded-xl bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800/50 px-4 py-3">
                    <svg className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/></svg>
                    <p className="text-sm text-green-700 dark:text-green-400">{resendNotice}</p>
                  </div>
                )}

                <form onSubmit={handleVerify} className="space-y-5">
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between">
                      <label className="block text-xs font-semibold tracking-widest uppercase text-neutral-500 dark:text-neutral-400">
                        Verification Code
                      </label>
                      <span className={`text-xs font-semibold tabular-nums ${codeExpired ? "text-red-500" : secondsLeft <= 30 ? "text-yellow-500" : "text-neutral-400"}`}>
                        {codeExpired ? "Expired" : formatTime(secondsLeft)}
                      </span>
                    </div>
                    <div className="flex items-center justify-center gap-2" onPaste={handleOtpPaste}>
                      {otp.map((digit, i) => (
                        <input
                          key={i}
                          ref={(el) => (otpRefs.current[i] = el)}
                          type="text"
                          inputMode="numeric"
                          maxLength={1}
                          value={digit}
                          onChange={(e) => handleOtpChange(i, e.target.value)}
                          onKeyDown={(e) => handleOtpKeyDown(i, e)}
                          className="w-11 h-13 sm:w-12 sm:h-12 text-center text-lg font-bold rounded-xl border bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 outline-none transition-all duration-200 border-neutral-200 dark:border-neutral-700 focus:border-green-500 focus:ring-2 focus:ring-green-500/20"
                          style={{ height: "3.25rem" }}
                        />
                      ))}
                    </div>
                    {codeExpired && (
                      <p className="text-xs text-red-500 text-center pt-1">This code has expired. Tap resend below for a new one.</p>
                    )}
                  </div>

                  <button
                    type="submit" disabled={verifying}
                    className={`w-full py-3.5 sm:py-3 rounded-xl text-sm font-semibold text-white transition-all duration-200 tracking-wide touch-manipulation ${verifying ? "bg-green-400 cursor-not-allowed opacity-70" : "bg-green-500 hover:bg-green-600 active:scale-[0.98] shadow-md shadow-green-500/25 hover:shadow-lg hover:shadow-green-500/30"}`}
                  >
                    {verifying ? (
                      <span className="flex items-center justify-center gap-2">
                        <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
                        Verifying…
                      </span>
                    ) : "Verify Account"}
                  </button>
                </form>

                <p className="mt-5 text-center text-sm text-neutral-500 dark:text-neutral-400">
                  Didn't get a code?{" "}
                  <button type="button" disabled={resending} onClick={handleResend} className="text-green-600 dark:text-green-400 font-semibold hover:underline touch-manipulation disabled:opacity-50">
                    {resending ? "Sending…" : "Resend code"}
                  </button>
                </p>

                <p className="mt-2 text-center text-sm">
                  <button type="button" onClick={handleBackToForm} className="text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 hover:underline touch-manipulation">
                    Use a different email
                  </button>
                </p>
              </>
            )}

            {/* ============== SUCCESS ============== */}
            {successMessage && (
              <div className="flex items-center gap-2.5 rounded-xl bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800/50 px-4 py-3">
                <svg className="w-4 h-4 text-green-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/></svg>
                <p className="text-sm text-green-700 dark:text-green-400 font-medium">{successMessage}</p>
              </div>
            )}
          </div>

          {/* Bottom note — no redundant links since they're already in the checkbox */}
          <p className="mt-6 text-center text-xs text-neutral-400 dark:text-neutral-500">
            © {new Date().getFullYear()} Metisel. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AccountView;